import { useState } from 'react';
import { Alert, Button, Card, Descriptions, Input, Modal, Space, Tag, Typography, message } from 'antd';
import { CheckCircleOutlined, StopOutlined, UserSwitchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../../store/authStore';
import { useDermatoWizard } from '../DermatoWizardContext';
import dermatoApi, { ScreeningConclusion } from '../../../../api/dermatoApi';
import flowApi from '../../../../api/flowApi';

const { Text, Paragraph, Title } = Typography;

/** Decizia de participare, păstrată în `form.generalData` (JSONB). */
interface ScreeningParticipationRecord {
  decision: 'ACCEPTED' | 'REFUSED';
  decidedAt: string;
  decidedBy: string;
}

/**
 * 2.9.3 — PAS 3 „Accept / refuz participare" (specificația tehnică) + §2 și
 * §3.1 din documentul medical („1. Traseul pacientului – pe scurt"):
 * echipa de screening înregistrează acceptul/refuzul LA ÎNROLARE, înainte de
 * chestionarul de anamneză și risc.
 *
 * - Accept → decizia se consemnează și se continuă cu chestionarul (pasul 2).
 * - Refuz  → verdict `SCREENING_REFUZAT` („EXCLUS DIN PROGRAM") + episod închis
 *   (tranziția `DERMATO_REFUSE_SCREENING`: ENROLLED/SCREENING → CLOSED).
 *   Data refuzului e `evaluatedAt`; motivul, dacă e declarat, e `observations`.
 */
const ScreeningParticipation = () => {
  const { patient, form, screeningResult, saveForm, refreshScreeningResult } = useDermatoWizard();
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [reason, setReason] = useState('');

  const refused = screeningResult?.screeningConclusion === ScreeningConclusion.SCREENING_REFUZAT;

  const record = (form?.generalData?.['screeningParticipation'] ?? null) as
    | ScreeningParticipationRecord
    | null;
  const accepted = record?.decision === 'ACCEPTED';

  const decidedBy = user?.email ?? 'echipa de screening';

  /** Acceptul participării — se consemnează și se continuă cu chestionarul. */
  const handleAccept = async () => {
    if (!form || !patient) return;
    try {
      setBusy(true);
      const saved = await saveForm({
        generalData: {
          ...(form.generalData ?? {}),
          screeningParticipation: {
            decision: 'ACCEPTED',
            decidedAt: new Date().toISOString(),
            decidedBy,
          } satisfies ScreeningParticipationRecord,
        },
      });
      if (!saved) return;
      message.success('Acceptarea participării a fost înregistrată. Continuă cu chestionarul de anamneză și risc.');
      navigate(`/patients/${patient.id}/dermato-wizard/2`);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Refuzul participării — verdict SCREENING_REFUZAT („EXCLUS DIN PROGRAM")
   * + închiderea episodului ca refuzat (PAS 3: „închiderea episodului ca refuzat").
   */
  const handleRefuse = async () => {
    if (!form) return;
    const trimmed = reason.trim();
    try {
      setBusy(true);
      // 1. verdictul „EXCLUS DIN PROGRAM" (data refuzului = evaluatedAt).
      await dermatoApi.screeningResult.upsert(form.id, {
        evaluationSummary: {
          pas: '3 — Accept / refuz participare',
          decizie: 'REFUZ',
          motivDeclarat: trimmed.length > 0,
        },
        screeningConclusion: ScreeningConclusion.SCREENING_REFUZAT,
        recommendation: 'Pacient exclus din programul de screening (EXCLUS DIN PROGRAM).',
        observations: trimmed.length > 0 ? trimmed : undefined,
        referralToBiopsy: false,
        evaluatedBy: decidedBy,
      });
      // 2. închiderea episodului ca refuzat.
      await flowApi.advance(
        'dermato',
        form.id,
        'CLOSED',
        trimmed.length > 0 ? `Refuz de participare: ${trimmed}` : 'Refuz de participare la programul de screening.'
      );
      await saveForm({
        generalData: {
          ...(form.generalData ?? {}),
          screeningParticipation: {
            decision: 'REFUSED',
            decidedAt: new Date().toISOString(),
            decidedBy,
          } satisfies ScreeningParticipationRecord,
        },
      });
      await refreshScreeningResult();
      message.warning('Refuz înregistrat. Pacientul este EXCLUS DIN PROGRAM — episodul a fost închis.');
    } catch (e: any) {
      const apiMsg = e?.response?.data?.message;
      message.error(Array.isArray(apiMsg) ? apiMsg.join(', ') : apiMsg ?? 'Nu s-a putut înregistra refuzul participării.');
    } finally {
      setBusy(false);
      setModalOpen(false);
    }
  };

  if (refused) {
    return (
      <Card
        data-testid="excluded-panel"
        title={
          <Space>
            <StopOutlined style={{ color: '#cf1322' }} />
            <span>EXCLUS DIN PROGRAM</span>
          </Space>
        }
        style={{ borderColor: '#ffa39e' }}
      >
        <Alert
          type="error"
          showIcon
          message="Pacientul a refuzat participarea la programul de screening."
          description="Episodul a fost închis ca refuzat (starea SCREENING REFUZAT). Pașii de evaluare nu mai pot fi parcurși; pacientul poate reveni în program printr-un episod nou de evaluare."
          style={{ marginBottom: 12 }}
        />
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Data refuzului">
            {screeningResult?.evaluatedAt
              ? new Date(screeningResult.evaluatedAt).toLocaleString('ro-RO')
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Înregistrat de">{screeningResult?.evaluatedBy ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Motiv" span={2}>
            {screeningResult?.observations?.trim() ? screeningResult.observations : '— (motiv ne-declarat)'}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    );
  }

  return (
    <Card
      data-testid="screening-participation"
      title={
        <Space>
          <UserSwitchOutlined style={{ color: '#1890ff' }} />
          <span>Accept / refuz participare la screening</span>
        </Space>
      }
      extra={<Tag color={accepted ? 'green' : 'default'}>{accepted ? 'Acceptat' : 'Nedeclarat'}</Tag>}
    >
      <Paragraph type="secondary">
        Pasul anterior chestionarului de anamneză și risc: echipa de screening consemnează acceptul sau
        refuzul pacientului de a participa la program (refuzul oprește traseul — „EXCLUS DIN PROGRAM").
      </Paragraph>

      {accepted && record && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 12 }}
          message="Acceptarea participării a fost înregistrată."
          description={`Data: ${new Date(record.decidedAt).toLocaleString('ro-RO')} · de către: ${record.decidedBy}`}
        />
      )}

      <Space wrap>
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          data-testid="accept-participation"
          loading={busy}
          disabled={refused}
          onClick={() => void handleAccept()}
        >
          Accept participarea și continuă cu chestionarul
        </Button>
        <Button
          danger
          icon={<StopOutlined />}
          data-testid="refuse-participation"
          loading={busy}
          disabled={refused}
          onClick={() => setModalOpen(true)}
        >
          Refuz participarea (exclus din program)
        </Button>
      </Space>

      <Modal
        open={modalOpen}
        title="Confirmă refuzul participării"
        okText="Înregistrează refuzul"
        okButtonProps={{ danger: true, loading: busy }}
        cancelText="Anulează"
        onCancel={() => setModalOpen(false)}
        onOk={() => void handleRefuse()}
      >
        <Paragraph>
          Se va înregistra refuzul pacientului, iar episodul va fi închis cu starea
          <Text strong> SCREENING REFUZAT („EXCLUS DIN PROGRAM")</Text>. Evaluarea clinică nu va mai
          continua pe acest episod.
        </Paragraph>
        <Title level={5} style={{ marginBottom: 4 }}>Motivul refuzului (opțional)</Title>
        <Input.TextArea
          rows={3}
          value={reason}
          data-testid="refuse-reason"
          placeholder="ex: refuză evaluarea, nu dorește proceduri invazive…"
          onChange={(e) => setReason(e.target.value)}
        />
      </Modal>
    </Card>
  );
};

export default ScreeningParticipation;
